import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  WarningAmber as WarningIcon,
  RemoveCircleOutline as RemoveSuppressionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { invitationsAPI, emailAdminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import AuditLogDetailDialog from '../components/AuditLogDetailDialog/AuditLogDetailDialog';

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  message?: string;
  notes?: string;
  expiresAt: string;
  createdAt: string;
  invitedBy: {
    id: string;
    email: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
  };
  metadata?: {
    message?: string;
    notes?: string;
  };
  acceptedAt?: string;
  acceptedByUser?: {
    id: string;
    email: string;
  };
}

interface SuppressedAddress {
  email: string;
  suppressed: boolean;
  reason?: string;
  createdAt?: string;
}

const AdminInvitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [tokenMap, setTokenMap] = useState<Record<string, string>>({});

  // Suppression list state
  const [suppressedAddresses, setSuppressedAddresses] = useState<SuppressedAddress[]>([]);
  const [suppressionExpanded, setSuppressionExpanded] = useState(false);
  const [, setSuppressionLoading] = useState(false);
  const [removingAddress, setRemovingAddress] = useState<string | null>(null);

  // Delivery log state
  const [deliveryLogs, setDeliveryLogs] = useState<any[]>([]);
  const [deliveryLogsLoading, setDeliveryLogsLoading] = useState(false);
  const [deliveryLogExpanded, setDeliveryLogExpanded] = useState(true);
  const [selectedDeliveryLog, setSelectedDeliveryLog] = useState<any | null>(null);
  const [deliveryLogDetailOpen, setDeliveryLogDetailOpen] = useState(false);

  // Test email state
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('USER');
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState('');

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;

      const response = await invitationsAPI.getAll(params);
      const invitationsData = response.data?.invitations || [];
      setInvitations(invitationsData);
    } catch (error: any) {
      toast.error('Failed to load invitations');
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadSuppressionList = useCallback(async () => {
    setSuppressionLoading(true);
    try {
      const response = await emailAdminAPI.getSuppressionList();
      const addresses = response.data?.suppressedAddresses || [];
      setSuppressedAddresses(addresses);
      if (addresses.length > 0) {
        setSuppressionExpanded(true);
      }
    } catch (error: any) {
      console.error('Error loading suppression list:', error);
    } finally {
      setSuppressionLoading(false);
    }
  }, []);

  const loadDeliveryLogs = useCallback(async () => {
    setDeliveryLogsLoading(true);
    try {
      const response = await emailAdminAPI.getDeliveryLog({ page: 1, limit: 25 });
      setDeliveryLogs(response.data?.data || []);
    } catch (error: any) {
      console.error('Error loading delivery logs:', error);
    } finally {
      setDeliveryLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvitations();
    loadSuppressionList();
    loadDeliveryLogs();
  }, [loadInvitations, loadSuppressionList, loadDeliveryLogs]);

  const handleCreateInvitation = async () => {
    if (!email) {
      toast.error('Email is required');
      return;
    }

    try {
      const response = await invitationsAPI.create({
        email,
        role,
        message: message || undefined,
        notes: notes || undefined,
      });
      const data = response.data;
      if (data.token && data.invitation?.id) {
        setTokenMap((prev) => ({ ...prev, [data.invitation.id]: data.token }));
      }
      toast.success('Invitation sent successfully!');
      setCreateDialogOpen(false);
      setEmail('');
      setMessage('');
      setNotes('');
      loadInvitations();
      loadSuppressionList();
      setTimeout(() => loadDeliveryLogs(), 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create invitation');
      console.error(error);
    }
  };

  const handleResend = async (id: string) => {
    try {
      const response = await invitationsAPI.resend(id);
      const data = response.data;
      if (data.token) {
        setTokenMap((prev) => ({ ...prev, [id]: data.token }));
      }
      toast.success('Invitation resent successfully! You can now copy the link.');
      loadInvitations();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
      console.error(error);
    }
  };

  const handleRevoke = async (id: string, emailAddr: string) => {
    if (!window.confirm(`Revoke invitation for ${emailAddr}?`)) return;

    try {
      await invitationsAPI.revoke(id);
      toast.success('Invitation revoked');
      loadInvitations();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to revoke invitation');
      console.error(error);
    }
  };

  const handleRemoveSuppression = async (emailAddr: string) => {
    setRemovingAddress(emailAddr);
    try {
      const response = await emailAdminAPI.removeSuppression(emailAddr);
      if (response.data?.removed) {
        toast.success(`Removed ${emailAddr} from email suppression list. Emails can now be delivered.`);
        loadSuppressionList();
      } else {
        toast.error(`Failed to remove ${emailAddr}: ${response.data?.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to remove suppression for ${emailAddr}`);
      console.error(error);
    } finally {
      setRemovingAddress(null);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Enter an email address');
      return;
    }
    setTestEmailSending(true);
    try {
      const response = await emailAdminAPI.sendTestEmail(testEmailAddress);
      if (response.data?.success) {
        toast.success(response.data.message || `Test email sent to ${testEmailAddress}`);
        setTimeout(() => loadDeliveryLogs(), 1000);
      } else {
        toast.error(response.data?.message || `Failed: ${response.data?.error}`);
      }
    } catch (error: any) {
      toast.error(`Test email failed: ${error.response?.data?.message || error.message}`);
      console.error(error);
    } finally {
      setTestEmailSending(false);
    }
  };

  const copyInvitationLink = (invitationId: string) => {
    const token = tokenMap[invitationId];
    if (!token) {
      toast.warning('Token not available. Click "Resend" first to generate a new link, then copy.');
      return;
    }
    const link = `${window.location.origin}/register?invite=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied to clipboard!');
  };

  const suppressedEmailSet = new Set(suppressedAddresses.map((a) => a.email.toLowerCase()));

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <PendingIcon color="warning" />;
      case 'ACCEPTED':
        return <CheckCircleIcon color="success" />;
      case 'EXPIRED':
        return <ErrorIcon color="error" />;
      case 'REVOKED':
        return <CancelIcon color="disabled" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ACCEPTED':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'REVOKED':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          User Invitations
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            size="small"
            label="Filter by Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="ACCEPTED">Accepted</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="REVOKED">Revoked</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { loadInvitations(); loadSuppressionList(); loadDeliveryLogs(); }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Invite User
          </Button>
        </Stack>
      </Stack>

      {/* SES Email Suppression List */}
      <Alert
        severity={suppressedAddresses.length > 0 ? 'warning' : 'success'}
        sx={{ mb: 3 }}
        action={
          suppressedAddresses.length > 0 ? (
            <IconButton
              aria-label={suppressionExpanded ? 'Collapse suppression list' : 'Expand suppression list'}
              size="small"
              onClick={() => setSuppressionExpanded(!suppressionExpanded)}
            >
              {suppressionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : undefined
        }
      >
        <AlertTitle>
          {suppressedAddresses.length > 0
            ? `Email Delivery Blocked for ${suppressedAddresses.length} Address${suppressedAddresses.length !== 1 ? 'es' : ''}`
            : 'Email Delivery Status: All Clear'}
        </AlertTitle>
        {suppressedAddresses.length > 0 ? (
          <>
            The following email addresses are on the AWS SES suppression list due to previous bounces.
            Emails to these addresses will be silently dropped even though they appear to send successfully.
            Remove them from the suppression list before resending invitations.
          </>
        ) : (
          <>
            No email addresses are currently blocked. All invitation emails should be delivered normally.
            The system automatically checks and clears the SES suppression list before each send.
          </>
        )}
        {suppressedAddresses.length > 0 && (
          <Collapse in={suppressionExpanded}>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Suppressed Since</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppressedAddresses.map((addr) => (
                    <TableRow key={addr.email}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {addr.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={addr.reason || 'BOUNCE'}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {addr.createdAt
                          ? format(new Date(addr.createdAt), 'MMM d, yyyy HH:mm')
                          : 'Unknown'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={
                            removingAddress === addr.email ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RemoveSuppressionIcon />
                            )
                          }
                          onClick={() => handleRemoveSuppression(addr.email)}
                          disabled={removingAddress === addr.email}
                        >
                          Remove & Unblock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        )}
      </Alert>

      {/* Test Email Sender */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <EmailIcon color="action" />
          <Typography variant="subtitle2" sx={{ minWidth: 120 }}>
            Test Delivery:
          </Typography>
          <TextField
            size="small"
            placeholder="recipient@example.com"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleSendTestEmail}
            disabled={testEmailSending || !testEmailAddress}
            startIcon={testEmailSending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send Test
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 5, mt: 0.5, display: 'block' }}>
          Send a test email to verify delivery. Check the delivery log below and the recipient's inbox (including spam/junk folder).
        </Typography>
      </Paper>

      {invitations.length === 0 && !loading ? (
        <Alert severity="info">
          No invitations found. Click "Invite User" to create one.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invited By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Message</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => {
                const isSuppressed = suppressedEmailSet.has(invitation.email.toLowerCase());
                return (
                  <TableRow
                    key={invitation.id}
                    sx={isSuppressed && invitation.status === 'PENDING' ? { bgcolor: 'warning.50' } : undefined}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getStatusIcon(invitation.status)}
                        <Chip
                          label={invitation.status}
                          size="small"
                          color={getStatusColor(invitation.status)}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{invitation.email}</Typography>
                        {isSuppressed && invitation.status === 'PENDING' && (
                          <Tooltip title="This email is on the SES suppression list. Emails will NOT be delivered. Remove from suppression list above, then resend.">
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={invitation.role} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {invitation.invitedBy.fullName && invitation.invitedBy.fullName !== 'Unknown User'
                        ? invitation.invitedBy.fullName
                        : invitation.invitedBy.email}
                    </TableCell>
                    <TableCell>{format(new Date(invitation.createdAt), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={new Date(invitation.expiresAt) < new Date() ? 'error' : 'textPrimary'}
                      >
                        {format(new Date(invitation.expiresAt), 'MMM d, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(invitation.metadata?.message || invitation.message) && (
                        <Tooltip title={invitation.metadata?.message || invitation.message || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {invitation.metadata?.message || invitation.message}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {invitation.status === 'PENDING' && (
                          <>
                            <Tooltip title={tokenMap[invitation.id] ? 'Copy invitation link' : 'Resend first to get a copyable link'}>
                              <IconButton
                                aria-label="Copy invitation link"
                                size="small"
                                onClick={() => copyInvitationLink(invitation.id)}
                                color={tokenMap[invitation.id] ? 'default' : 'warning'}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Resend invitation">
                              <IconButton
                                aria-label="Resend invitation"
                                size="small"
                                color="primary"
                                onClick={() => handleResend(invitation.id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke invitation">
                              <IconButton
                                aria-label="Revoke invitation"
                                size="small"
                                color="error"
                                onClick={() => handleRevoke(invitation.id, invitation.email)}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Email Delivery Log */}
      <Paper sx={{ mt: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, cursor: 'pointer' }}
          onClick={() => setDeliveryLogExpanded(!deliveryLogExpanded)}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <EmailIcon color="primary" />
            <Typography variant="h6">Email Delivery Log</Typography>
            <Chip label={`${deliveryLogs.length} recent`} size="small" variant="outlined" />
          </Stack>
          <IconButton aria-label={deliveryLogExpanded ? 'Collapse delivery log' : 'Expand delivery log'} size="small">
            {deliveryLogExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        <Collapse in={deliveryLogExpanded}>
          <Divider />
          {deliveryLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : deliveryLogs.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No email delivery logs yet. Logs will appear here after emails are sent.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>SES Message ID</TableCell>
                    <TableCell>Suppression</TableCell>
                    <TableCell align="right">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveryLogs.map((log) => {
                    const diag = log.metadata?.emailDiagnostics;
                    const suppressionCheck = diag?.suppressionCheck;
                    return (
                      <TableRow
                        key={log.id}
                        sx={{
                          bgcolor: log.success ? undefined : 'error.50',
                          '&:hover': { bgcolor: log.success ? 'action.hover' : 'error.100' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {format(new Date(log.createdAt), 'MMM d, HH:mm:ss')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {log.entityName || diag?.recipientEmail || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={diag?.subject || '-'}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {diag?.subject || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.success ? 'Sent' : 'Failed'}
                            size="small"
                            color={log.success ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={diag?.sesResponse?.messageId || 'N/A'}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {diag?.sesResponse?.messageId
                                ? diag.sesResponse.messageId.substring(0, 20) + '...'
                                : log.success ? 'N/A' : '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {suppressionCheck?.wasSuppressed ? (
                            <Chip
                              label={suppressionCheck.removed ? 'Was blocked, auto-cleared' : 'BLOCKED'}
                              size="small"
                              color={suppressionCheck.removed ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          ) : (
                            <Chip label="Clear" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View full diagnostics">
                            <IconButton
                              aria-label="View full diagnostics"
                              size="small"
                              onClick={() => {
                                setSelectedDeliveryLog(log);
                                setDeliveryLogDetailOpen(true);
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Paper>

      {/* Email Delivery Log Detail Dialog */}
      <AuditLogDetailDialog
        open={deliveryLogDetailOpen}
        onClose={() => {
          setDeliveryLogDetailOpen(false);
          setSelectedDeliveryLog(null);
        }}
        log={selectedDeliveryLog}
      />

      {/* Create Invitation Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              helperText="The user will receive an invitation email at this address"
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              helperText="Assigned role for the new user"
            >
              <MenuItem value="USER">User - Read-only stakeholder</MenuItem>
              <MenuItem value="CORPORATE_ADMIN">Corporate Admin - Manage specific entities</MenuItem>
              <MenuItem value="AUDITOR">Auditor - Read-only with audit logs</MenuItem>
              <MenuItem value="ADMIN">Admin - Organizational administrator</MenuItem>
              <MenuItem value="SUPER_ADMIN">Super Admin - Full system access</MenuItem>
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message (Optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              helperText="Personal message to include in the invitation email"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Internal Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              helperText="Admin-only notes (not visible to the user)"
            />
            <Alert severity="info">
              The invitation link will expire in 7 days. The user will be able to register using the
              link sent to their email.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvitation} startIcon={<SendIcon />}>
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminInvitations;
